import { sortedUniq, has, pickBy } from 'lodash-es'

const checkMembershipOverride = (
  membership,
  ageGroup,
  quantity,
  childLearningEvent
) => {
  const { adultMemberLimit, childrenAllowed } = membership
  if (ageGroup === 'adult') {
    if (childrenAllowed && childLearningEvent) {
      return false
    }
    return quantity >= adultMemberLimit
  }
  if (['child', 'youth'].includes(ageGroup)) {
    return !childrenAllowed
  }
}

const getAgeGroups = tickets => {
  const ageGroups = sortedUniq(
    tickets.map(ticket => [
      ticket.member ? 'member' : 'nonMember',
      ticket.pricingGroup == 'chicago' || ticket.pricingGroup == 'illinois'
        ? 'resident'
        : 'nonResident',
      ticket.ageGroup
    ])
  )
  return ageGroups
}

const sumTickets = tickets => {
  return tickets.reduce((sum, ticket) => {
    return sum + ticket.quantity
  }, 0)
}

const countGenericTicketQuantity = quantitiesObj => {
  let baseEventGenericTickets = 0

  Object.entries(quantitiesObj.nonMember.nonResident).forEach(
    ([ageGroup, quantityObject]) => {
      if (ageGroup.includes('generic')) {
        baseEventGenericTickets += quantityObject.quantity
      }
    }
  )
  return baseEventGenericTickets
}

const maximumsForDependentWithGreaterOrEqualAgeGroups = (
  dependentQuantitiesObject,
  dependencyQuantitiesObject,
  dependentQuantitySum,
  availability,
  membership,
  childLearningEvent
) => {
  let maximums = {
    member: {
      nonResident: {}
    },
    nonMember: {
      resident: {},
      nonResident: {}
    }
  }

  Object.keys(dependencyQuantitiesObject).forEach(memberKey => {
    Object.keys(dependencyQuantitiesObject[memberKey]).forEach(residentKey => {
      Object.values(
        dependencyQuantitiesObject[memberKey]?.[residentKey]
      ).forEach(quantityObject => {
        let totalQuantity = 0
        let ageGroups = []
        if (dependentQuantitiesObject[memberKey]?.[residentKey]) {
          Object.entries(
            dependentQuantitiesObject[memberKey][residentKey]
          ).forEach(([ageGroup, dependentQuantityObject]) => {
            if (
              dependentQuantityObject.lowerLimit >= quantityObject.lowerLimit &&
              dependentQuantityObject.upperLimit <= quantityObject.upperLimit
            ) {
              totalQuantity += dependentQuantityObject.quantity
              ageGroups.push(ageGroup)
            }
          })
        }
        if (
          totalQuantity < quantityObject.quantity &&
          dependentQuantitySum < availability
        ) {
          ageGroups.forEach(ageGroup => {
            if (memberKey === 'member') {
              const membershipCheckAgeGroup = ageGroups.includes('adult')
                ? 'adult'
                : 'child'
              maximums[memberKey][residentKey][ageGroup] =
                checkMembershipOverride(
                  membership,
                  membershipCheckAgeGroup,
                  totalQuantity,
                  childLearningEvent
                )
            } else {
              maximums[memberKey][residentKey][ageGroup] = false
            }
          })
        } else {
          ageGroups.forEach(ageGroup => {
            maximums[memberKey][residentKey][ageGroup] = true
          })
        }
      })
    })
  })
  return maximums
}

const maximumsForDependentWithLessAgeGroups = (
  dependentQuantitiesObject, // dependent == Add On
  dependencyQuantitiesObject, // dependency == Base Event
  dependentQuantitySum, // dependentQuantitySum is the total number of tickets added to the Add On
  availability, // total add on tickets availability for sale
  membership,
  childLearningEvent
) => {
  const baseEventGenericTickets = countGenericTicketQuantity(
    dependencyQuantitiesObject
  )

  // needs to return age group true or false
  // The add on adding increment button is disabled for a specific age group when this object returns an age_group: true
  let maximums = {
    member: {
      nonResident: {}
    },
    nonMember: {
      nonResident: {},
      resident: {}
    }
  }

  // This object holds the logic used to build the return object above.
  // This object can't be returned as is because the other information is extraneous to other components like the cart page
  let buildMaximums = {
    combinedNonMember: {}, // combinedNonMember sums resident and nonResident age group counts
    member: {
      nonResident: {}
    },
    nonMember: {
      resident: {},
      nonResident: {}
    }
  }
  let specificSetHitMax = []
  let makeAllOfThisAgeGroupTrue = ''

  Object.keys(dependentQuantitiesObject).forEach(addOnMemberKey => {
    Object.keys(dependentQuantitiesObject[addOnMemberKey]).forEach(
      addOnResidentKey => {
        Object.entries(
          dependentQuantitiesObject[addOnMemberKey][addOnResidentKey]
        ).forEach(([addOnAgeGroup, addOnQuantityObj]) => {
          if (dependentQuantitySum === availability) {
            maximums[addOnMemberKey][addOnResidentKey][addOnAgeGroup] = true
            return
          } else {
            maximums[addOnMemberKey][addOnResidentKey][addOnAgeGroup] = false
          }
          let checkQuantity = 0

          buildMaximums['combinedNonMember'][addOnAgeGroup + 'AddOn'] == null
            ? (buildMaximums['combinedNonMember'][addOnAgeGroup + 'AddOn'] =
                addOnQuantityObj.quantity)
            : (buildMaximums['combinedNonMember'][addOnAgeGroup + 'AddOn'] +=
                addOnQuantityObj.quantity)

          if (
            dependencyQuantitiesObject[addOnMemberKey] &&
            dependencyQuantitiesObject[addOnMemberKey][addOnResidentKey]
          ) {
            Object.entries(
              dependencyQuantitiesObject[addOnMemberKey][addOnResidentKey]
            ).forEach(([baseEventAgeGroup, baseTicketObj]) => {
              // we have an overide here for 21+ events (upperLimit === upperLimit)
              if (
                (!baseEventAgeGroup.includes('generic') &&
                  baseTicketObj.lowerLimit >= addOnQuantityObj.lowerLimit &&
                  baseTicketObj.upperLimit <= addOnQuantityObj.upperLimit) ||
                (!baseEventAgeGroup.includes('generic') &&
                  addOnQuantityObj.upperLimit === baseTicketObj.upperLimit)
              ) {
                checkQuantity += baseTicketObj.quantity

                buildMaximums[addOnMemberKey][addOnResidentKey][
                  addOnAgeGroup + 'AddOn'
                ] = addOnQuantityObj.quantity
                buildMaximums[addOnMemberKey][addOnResidentKey][
                  addOnAgeGroup + 'Base'
                ] = checkQuantity

                buildMaximums['combinedNonMember'][addOnAgeGroup + 'Base'] ==
                null
                  ? (buildMaximums['combinedNonMember'][
                      addOnAgeGroup + 'Base'
                    ] = baseTicketObj.quantity)
                  : (buildMaximums['combinedNonMember'][
                      addOnAgeGroup + 'Base'
                    ] += baseTicketObj.quantity)

                buildMaximums[addOnMemberKey][addOnResidentKey][
                  addOnAgeGroup + 'Difference'
                ] = checkQuantity - addOnQuantityObj.quantity

                if (addOnMemberKey === 'member') {
                  buildMaximums[addOnMemberKey][addOnResidentKey][
                    addOnAgeGroup
                  ] = checkMembershipOverride(
                    membership,
                    addOnAgeGroup,
                    addOnQuantityObj.quantity,
                    childLearningEvent
                  )
                }
              }
            })
          }
        })
      }
    )
  })

  // if resident difference is positive, make the non resident max=true
  // if combined addon - combined base = #generic tickets no one else can use generic tickets

  let nonMnonRAdultD = buildMaximums.nonMember.nonResident.adultDifference
  let nonMRAdultD = buildMaximums.nonMember.resident.adultDifference
  let nonMnonRChildD = buildMaximums.nonMember.nonResident.childDifference
  let nonMRChildD = buildMaximums.nonMember.resident.childDifference

  let arrayOfD = [
    { nonMnonRAdultD: nonMnonRAdultD },
    { nonMRAdultD: nonMRAdultD },
    { nonMnonRChildD: nonMnonRChildD },
    { nonMRChildD: nonMRChildD }
  ]

  let dLessThanZero = arrayOfD.filter(set => {
    let [[_, v]] = Object.entries(set)
    return v < 0
  })

  let usedGenericTickets = dLessThanZero.length

  const checkZeros = () => {
    if (nonMRAdultD == 0) {
      specificSetHitMax.push(['nonMember', 'resident', 'adult'])
    }

    if (nonMnonRAdultD == 0) {
      specificSetHitMax.push(['nonMember', 'nonResident', 'adult'])
    }

    if (nonMRChildD == 0) {
      specificSetHitMax.push(['nonMember', 'resident', 'child'])
    }

    if (nonMnonRChildD == 0) {
      specificSetHitMax.push(['nonMember', 'nonResident', 'child'])
    }
  }

  if (baseEventGenericTickets === 0) {
    checkZeros()
  } else {
    dLessThanZero.forEach(set => {
      let [[k, _]] = Object.entries(set)
      if (usedGenericTickets == baseEventGenericTickets) {
        checkZeros()

        if (k.includes('Adult')) {
          if (k.includes('nonR')) {
            specificSetHitMax.push(['nonMember', 'nonResident', 'adult'])
            if (nonMRAdultD == 0) {
              specificSetHitMax.push(['nonMember', 'resident', 'adult'])
            }
          } else {
            specificSetHitMax.push(['nonMember', 'resident', 'adult'])
            if (nonMnonRAdultD == 0) {
              specificSetHitMax.push(['nonMember', 'nonResident', 'adult'])
            }
          }
        } else {
          if (k.includes('nonR')) {
            specificSetHitMax.push(['nonMember', 'nonResident', 'child'])
            if (nonMRChildD == 0) {
              specificSetHitMax.push(['nonMember', 'resident', 'child'])
            }
          } else {
            specificSetHitMax.push(['nonMember', 'resident', 'child'])
            if (nonMnonRChildD == 0) {
              specificSetHitMax.push(['nonMember', 'nonResident', 'child'])
            }
          }
        }
      }
    })
  }

  if (specificSetHitMax.length > 0) {
    specificSetHitMax.forEach(ar => {
      maximums[ar[0]][ar[1]][ar[2]] = true
    })
  }
  if (makeAllOfThisAgeGroupTrue !== '') {
    if (
      buildMaximums['combinedNonMember'][makeAllOfThisAgeGroupTrue + 'Base'] +
        baseEventGenericTickets -
        buildMaximums['combinedNonMember'][
          makeAllOfThisAgeGroupTrue + 'AddOn'
        ] ===
      0
    ) {
      Object.keys(buildMaximums.nonMember).forEach(residency => {
        maximums.nonMember[residency][makeAllOfThisAgeGroupTrue] = true
      })
    }
  }
  return maximums
}

const dependencyOnlyMaximums = (
  dependencyQuantitiesObject,
  membership,
  totalQuantity,
  availability,
  childLearningEvent
) => {
  let maximums = {
    member: {
      nonResident: {}
    },
    nonMember: {
      nonResident: {},
      resident: {}
    }
  }

  Object.keys(dependencyQuantitiesObject).forEach(memberKey => {
    Object.keys(dependencyQuantitiesObject[memberKey]).forEach(residentKey => {
      Object.entries(
        dependencyQuantitiesObject[memberKey][residentKey]
      ).forEach(([ageGroup, quantityObject]) => {
        if (totalQuantity === availability) {
          maximums[memberKey][residentKey][ageGroup] = true
          return
        }
        if (memberKey === 'member') {
          maximums[memberKey][residentKey][ageGroup] = checkMembershipOverride(
            membership,
            ageGroup,
            quantityObject.quantity,
            childLearningEvent
          )
        } else {
          maximums[memberKey][residentKey][ageGroup] = false
        }
      })
    })
  })
  return maximums
}

export const sumAgeGroupTickets = (tickets, ageGroups) => {
  const quantitiesObject = {}
  ageGroups.forEach(([memberKey, residentKey, ageKey]) => {
    if (!quantitiesObject[memberKey]) {
      quantitiesObject[memberKey] = {}
    }
    if (!quantitiesObject[memberKey][residentKey]) {
      quantitiesObject[memberKey][residentKey] = {}
    }
    quantitiesObject[memberKey][residentKey][ageKey] = { quantity: 0 }
  })
  tickets.forEach(ticket => {
    const memberKey = ticket.member ? 'member' : 'nonMember'
    const residentKey =
      ticket.pricingGroup == 'chicago' || ticket.pricingGroup == 'illinois'
        ? 'resident'
        : 'nonResident'
    const ticketQuantityObject =
      quantitiesObject[memberKey][residentKey][ticket.ageGroup]
    const newTicketQuantityObject = {
      quantity: (ticketQuantityObject.quantity += ticket.quantity),
      upperLimit: ticket.upperLimit,
      lowerLimit: ticket.lowerLimit
    }
    quantitiesObject[memberKey][residentKey][ticket.ageGroup] =
      newTicketQuantityObject
  })
  return quantitiesObject
}

const condenseQuantities = (target, sourceKey, targetKey, nested = false) => {
  if (nested) {
    Object.entries(target[sourceKey].nonResident).forEach(
      ([ageGroup, quantityObject]) => {
        if (target[targetKey].nonResident[ageGroup]) {
          target[targetKey].nonResident[ageGroup].quantity +=
            quantityObject.quantity
        } else {
          target[targetKey].nonResident[ageGroup] = quantityObject
        }
        quantityObject.quantity = 0
      }
    )
  } else {
    Object.entries(target[sourceKey]).forEach(([ageGroup, quantityObject]) => {
      if (target[targetKey][ageGroup]) {
        target[targetKey][ageGroup].quantity += quantityObject.quantity
      } else {
        target[targetKey][ageGroup] = quantityObject
      }
      quantityObject.quantity = 0
    })
  }
}

export const setHitMax = (
  dependentTickets, // Add On
  dependencyTickets, // Base Event
  membership,
  availability,
  childLearningEvent = false,
  hitEmployeeMax = false,
  hitMembershipMax = false,
  hitSpecificConstituencyMax = null
) => {
  let maximums = null
  const dependencyAgeGroups = getAgeGroups(dependencyTickets)

  // This is the number of adult/child/genericN tickets for the member and non-member group
  const dependencyQuantitiesObject = sumAgeGroupTickets(
    dependencyTickets,
    dependencyAgeGroups
  )

  const dependencyQuantitySum = sumTickets(dependencyTickets)

  if (!dependentTickets) {
    maximums = dependencyOnlyMaximums(
      dependencyQuantitiesObject,
      membership,
      dependencyQuantitySum,
      availability,
      childLearningEvent
    )

    if (hitEmployeeMax) maximums.nonMember.nonResident.employee = hitEmployeeMax

    if (hitSpecificConstituencyMax?.length > 0)
      maximums.nonMember.nonResident.constituency = hitSpecificConstituencyMax

    if (hitMembershipMax) maximums.member.nonResident['adult'] = true

    return maximums
  }

  const dependentQuantitySum = sumTickets(dependentTickets)
  const dependentAgeGroups = getAgeGroups(dependentTickets)
  const dependentQuantitiesObject = sumAgeGroupTickets(
    dependentTickets,
    dependentAgeGroups
  )

  // If the dependent object (ie: Add On) only has NON Member pricing but the dependency object (ie: base event) HAS member pricing,
  // condense the member quantities into the non-member quantities.
  if (
    !has(dependentQuantitiesObject, 'member') &&
    has(dependencyQuantitiesObject, 'member') &&
    has(dependencyQuantitiesObject, 'nonMember')
  ) {
    condenseQuantities(dependencyQuantitiesObject, 'member', 'nonMember', true)
  }

  // If the dependent object (ie: Add On) only has nonResident pricing but the dependency object (ie: base event) HAS resident pricing,
  // condense the base event's resident quantities into the nonResident quantities.
  if (
    has(dependencyQuantitiesObject.nonMember, 'resident') &&
    Object.values(dependencyQuantitiesObject.nonMember.resident).some(
      ticket => ticket.quantity !== 0
    ) &&
    !has(dependentQuantitiesObject.nonMember, 'resident')
  ) {
    condenseQuantities(
      dependencyQuantitiesObject.nonMember,
      'resident',
      'nonResident'
    )
  }

  //infant age groups threw off our ageGroups comparison logic
  const nonInfantDependencyAgeGroups = dependencyAgeGroups.filter(
    ageGroup => !ageGroup.includes('infant')
  )
  if (
    nonInfantDependencyAgeGroups.length <= dependentAgeGroups.length &&
    dependentTickets.some(ticket => ticket.ageGroup.includes('youth'))
  ) {
    maximums = maximumsForDependentWithGreaterOrEqualAgeGroups(
      dependentQuantitiesObject, // addon
      dependencyQuantitiesObject, // base event
      dependentQuantitySum,
      availability,
      membership,
      childLearningEvent
    )
  } else {
    // Put this logic in here to filter out infant tickets from the dependency (ie: Base event).
    // This fixes the issue of being able to add 3 photo add ons to an event with 1 Adult, Child, Infant.
    // This whole setHitMax function needs to be evaluated from a business perspective.
    Object.keys(dependencyQuantitiesObject).forEach(memberKey => {
      Object.keys(dependencyQuantitiesObject[memberKey]).forEach(
        residentKey => {
          dependencyQuantitiesObject[memberKey][residentKey] = pickBy(
            dependencyQuantitiesObject[memberKey][residentKey],
            function (key, value) {
              return value !== 'infant'
            }
          )
        }
      )
    })
    maximums = maximumsForDependentWithLessAgeGroups(
      dependentQuantitiesObject,
      dependencyQuantitiesObject,
      dependentQuantitySum,
      availability,
      membership,
      childLearningEvent,
      dependencyTickets
    )
  }

  if ('employee' in maximums.nonMember.nonResident) {
    maximums.nonMember.nonResident.employee = hitEmployeeMax
  }

  if (hitMembershipMax) {
    maximums.member.nonResident['adult'] = true
  }

  return maximums
}
